import { ChevronsUpDown, Check } from "lucide-react";
import type { JSX } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@mailflare/ui";
import type { MailboxWithCounts } from "@mailflare/shared";

import { routes } from "@/features/mailboxes/routes";

export interface MailboxSwitcherProps {
  readonly mailboxes: readonly MailboxWithCounts[];
  readonly current: MailboxWithCounts | null;
}

export function MailboxSwitcher({ mailboxes, current }: MailboxSwitcherProps): JSX.Element {
  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="max-w-48 gap-2"
          aria-label="Switch mailbox"
          data-testid="mailbox-switcher"
          disabled={mailboxes.length === 0}
        >
          <span className="truncate">{current?.address ?? "No mailbox"}</span>
          <ChevronsUpDown aria-hidden className="size-3.5 shrink-0 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Mailboxes</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {mailboxes.map((mailbox) => {
          const isCurrent = mailbox.id === current?.id;
          return (
            <DropdownMenuItem key={mailbox.id} onSelect={() => navigate(routes.inbox(mailbox.id))}>
              <span className="flex-1 truncate">{mailbox.address}</span>
              {isCurrent ? <Check aria-hidden className="size-4" /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
